import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code as ConnectCode, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraEyebrow, SpecraHeading, SpecraIconTile, SpecraInlineBanner, SpecraKeyValueRow, SpecraSurface, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Download, FileArchive, FileText, FolderPlus, PencilLine, Sheet } from 'lucide-react';
import { useRef, useState } from 'react';
import { asOfficeDocType, useOfficeDocumentDrawer } from '../../../contexts/OfficeDocumentDrawerContext';
import { documentsApi } from '../../../lib/api/documentsApi.connect';
import { artifactClient } from '../../../lib/connect/clients';
import { sameOriginStorageUrl } from '../../../lib/storageUrl';
import { formatFileSize } from '../../../utils/formatFileSize';
/** The link lives only long enough to start the download; every click mints a fresh one. */ const LINK_TTL_SECONDS = 60;
function FormatIcon({ format }) {
    if (format === 'zip') return /*#__PURE__*/ _jsx(FileArchive, {
        className: "h-4 w-4"
    });
    if (format === 'xlsx' || format === 'csv') return /*#__PURE__*/ _jsx(Sheet, {
        className: "h-4 w-4"
    });
    return /*#__PURE__*/ _jsx(FileText, {
        className: "h-4 w-4"
    });
}
function count(n, one, many) {
    return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}
/** One line on what the file holds: "2,048 rows · 312 files · 1.2 GB". */ export function downloadContents(output) {
    const parts = [];
    if (typeof output.rows === 'number') parts.push(count(output.rows, 'row', 'rows'));
    if (output.format === 'zip' && output.files) {
        parts.push(count(output.files.included ?? 0, 'file', 'files'));
        if (output.files.links) parts.push(count(output.files.links, 'link', 'links'));
    }
    if (output.sizeBytes) parts.push(formatFileSize(output.sizeBytes));
    return parts.join(' · ');
}
/** What is not in the file, in plain words; null when nothing is missing. */ export function downloadGaps(output) {
    const gaps = [];
    if (output.receipt && output.receipt.complete === false) {
        gaps.push(`Not every record was read: ${output.receipt.summary || 'the read stopped early'}.`);
    }
    const skipped = output.files?.skipped ?? 0;
    if (skipped > 0) {
        gaps.push(`${count(skipped, 'file was', 'files were')} left out — manifest.csv in the zip lists each one and why.`);
    }
    return gaps.length ? gaps.join(' ') : null;
}
function downloadErrorMessage(err) {
    if (err instanceof ConnectError) {
        switch(err.code){
            case ConnectCode.PermissionDenied:
                return 'Only the person who asked for this file can download it.';
            case ConnectCode.NotFound:
                return 'This file no longer exists. Ask the assistant to make it again.';
            default:
        }
    }
    return 'The download could not start. Try again.';
}
/** The Office type a download can open as in the editor, or null. */ export function downloadOfficeType(output) {
    const ext = (output.filename ?? '').split('.').pop()?.toLowerCase();
    return asOfficeDocType(output.format) ?? asOfficeDocType(ext);
}
/**
 * The download card: names the file and what it holds, says it is kept only
 * in this chat, and downloads it on click. There is no Accept — nothing is
 * saved to Documents, program Docs or any list.
 */ export function DownloadFileCard({ output }) {
    const [busy, setBusy] = useState(false);
    const [started, setStarted] = useState(false);
    const [error, setError] = useState(null);
    const busyRef = useRef(false);
    const filename = output.filename || 'Download';
    const contents = downloadContents(output);
    const gaps = downloadGaps(output);
    const ready = Boolean(output.artifactId);
    const officeType = downloadOfficeType(output);
    const { openOffice } = useOfficeDocumentDrawer();
    const [opening, setOpening] = useState(false);
    const [opened, setOpened] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    // Copy the file into the user's Documents. Keyed by the file, so Save then
    // Open (or a second click) reuses the same copy instead of making another.
    const copyToDocuments = async (officeDocType)=>{
        const { url } = await artifactClient.generateDownloadUrl({
            artifactId: output.artifactId,
            ttlSeconds: LINK_TTL_SECONDS
        });
        if (!url) throw new Error('no link');
        const resp = await fetch(sameOriginStorageUrl(url));
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return documentsApi.createDocument({
            name: filename,
            documentType: officeDocType,
            initialBytes: new Uint8Array(await resp.arrayBuffer()),
            operationId: `chat-download:${output.artifactId}`
        });
    };
    // Save to Documents: keeps the file past the chat, without opening it.
    const saveToDocuments = async ()=>{
        if (busyRef.current || !output.artifactId || !officeType) return;
        busyRef.current = true;
        setSaving(true);
        setError(null);
        try {
            await copyToDocuments(officeType);
            setSaved(true);
        } catch (err) {
            // A gone file says so; anything else is a failed save, not a download.
            setError(err instanceof ConnectError && err.code === ConnectCode.NotFound ? downloadErrorMessage(err) : 'The file could not be saved to Documents. Try again, or download it.');
        } finally{
            busyRef.current = false;
            setSaving(false);
        }
    };
    // Open in OnlyOffice: the editor works on Documents, so this saves the copy
    // first (the same one Save makes).
    const openInEditor = async ()=>{
        if (busyRef.current || !output.artifactId || !officeType) return;
        busyRef.current = true;
        setOpening(true);
        setError(null);
        try {
            const doc = await copyToDocuments(officeType);
            openOffice(doc.id, {
                title: filename,
                docType: officeType
            });
            setOpened(true);
            setSaved(true);
        } catch (err) {
            setError(err instanceof ConnectError ? downloadErrorMessage(err) : 'The file could not be opened in the editor. Try again, or download it.');
        } finally{
            busyRef.current = false;
            setOpening(false);
        }
    };
    const download = async ()=>{
        if (busyRef.current || !output.artifactId) return;
        busyRef.current = true;
        setBusy(true);
        setError(null);
        try {
            const { url } = await artifactClient.generateDownloadUrl({
                artifactId: output.artifactId,
                ttlSeconds: LINK_TTL_SECONDS
            });
            if (!url) throw new Error('no link');
            // The response is an attachment, so following the link saves the file
            // and the page stays where it is.
            const a = document.createElement('a');
            a.href = sameOriginStorageUrl(url);
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setStarted(true);
        } catch (err) {
            setError(downloadErrorMessage(err));
        } finally{
            busyRef.current = false;
            setBusy(false);
        }
    };
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        "data-testid": Testids.assistant.downloadFileCard,
        "data-state": ready ? 'ready' : 'error',
        elevation: "xs",
        className: "overflow-hidden",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start gap-3 px-4 pb-3 pt-3.5",
                children: [
                    /*#__PURE__*/ _jsx(SpecraIconTile, {
                        tone: gaps ? 'warning' : 'info',
                        children: /*#__PURE__*/ _jsx(FormatIcon, {
                            format: output.format
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                children: saved ? 'Download · saved to Documents' : 'Download · not saved to Documents'
                            }),
                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                size: "sm",
                                className: "mt-1 truncate",
                                title: filename,
                                "data-testid": Testids.assistant.downloadFileName,
                                children: filename
                            }),
                            contents ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "micro",
                                tone: "muted",
                                className: "mt-0.5 block",
                                children: contents
                            }) : null,
                            output.files?.links ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "micro",
                                tone: "muted",
                                className: "mt-0.5 block",
                                children: "Links are not files, so manifest.csv in the zip lists each with its address."
                            }) : null
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border px-4 py-2.5",
                children: /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                    label: "Kept",
                    value: saved ? 'A copy is in your Documents and stays there. The download here is deleted with the chat.' : officeType ? 'Only you can download it, from this chat. It is deleted when the chat is deleted, unless you save it to Documents.' : 'Only you can download it, from this chat. It is deleted when the chat is deleted.'
                })
            }),
            gaps ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "band",
                tone: "warning",
                "data-testid": Testids.assistant.downloadFileStatus,
                children: gaps
            }) : null,
            error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "band",
                tone: "danger",
                "data-testid": Testids.assistant.downloadFileStatus,
                children: error
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.assistant.downloadFileButton,
                        onClick: ()=>void download(),
                        disabled: !ready,
                        isLoading: busy,
                        iconLeft: /*#__PURE__*/ _jsx(Download, {
                            className: "h-4 w-4"
                        }),
                        children: "Download"
                    }),
                    officeType ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "outline",
                        "data-testid": Testids.assistant.downloadFileSaveButton,
                        onClick: ()=>void saveToDocuments(),
                        disabled: !ready || saved,
                        isLoading: saving,
                        iconLeft: /*#__PURE__*/ _jsx(FolderPlus, {
                            className: "h-4 w-4"
                        }),
                        children: saved ? 'Saved to Documents' : 'Save to Documents'
                    }) : null,
                    officeType ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "outline",
                        "data-testid": Testids.assistant.downloadFileOpenButton,
                        onClick: ()=>void openInEditor(),
                        disabled: !ready,
                        isLoading: opening,
                        iconLeft: /*#__PURE__*/ _jsx(PencilLine, {
                            className: "h-4 w-4"
                        }),
                        title: "Opens an editable copy in OnlyOffice; the copy is saved to your Documents",
                        children: "Open in OnlyOffice"
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        tone: "muted",
                        className: "ml-auto",
                        children: opened ? 'Opened — a copy is in your Documents.' : saved ? 'Saved — find it in Documents.' : started ? 'Download started.' : ready ? 'No Accept needed.' : 'This file is not available.'
                    })
                ]
            })
        ]
    });
}
